/**
 * Shared API Client
 * Used by both main app and admin portal
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

export interface ApiClientConfig {
  baseURL?: string
  getToken?: () => string | null
  onUnauthorized?: () => void
}

class ApiClient {
  private client: AxiosInstance
  private getToken?: () => string | null
  private onUnauthorized?: () => void

  constructor(config: ApiClientConfig = {}) {
    const baseURL = config.baseURL ?? import.meta.env.VITE_API_URL ?? 'http://localhost:8000'
    
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.getToken = config.getToken
    this.onUnauthorized = config.onUnauthorized

    this.setupInterceptors()
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = this.getToken ? this.getToken() : localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          if (this.onUnauthorized) {
            this.onUnauthorized()
          } else {
            localStorage.removeItem('token')
            // Redirect to login
            if (typeof window !== 'undefined') {
              window.location.href = '/login'
            }
          }
        }
        return Promise.reject(error)
      }
    )
  }

  get instance(): AxiosInstance {
    return this.client
  }

  // Convenience methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config)
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config)
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config)
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config)
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config)
  }
}

// Default instance (can be configured per app)
export const createApiClient = (config?: ApiClientConfig) => new ApiClient(config)

// Default export
export const apiClient = createApiClient()

